/**
 * personalEvents.repository — 개인 일정 users/{uid}/personal_events/{autoId}.
 *
 * - 문서 ID = 자동 ID(비즈니스 키 없음). 화면 key 도 이 id.
 * - 일정·할 일 공용: kind("event"|"todo") + done. 옛 문서(kind 없음)는 domain 에서 event 로 읽는다.
 * - calendar_id(선택, 2026-10-29) = 내 캘린더 문서 ID. 없으면 기본 캘린더.
 * - 실시간 목록은 orderBy 를 쓰지 않는다(serverTimestamp 확정 전 즉시 반영 문제) → 정렬은 domain sortEvents.
 * - rules: 본인만 읽기·쓰기. ⚠️ 2026-10-19 기준 rules 미배포 → permission-denied.
 *   호출부(hooks/usePersonalEvents)가 그 오류를 잡아 미리보기 로컬 저장으로 대체한다.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import { coercePersonalEvent, type PersonalEvent, type PersonalEventInput } from "@/domain/personalEvent";

const sub = (uid: string) => collection(db, "users", uid, "personal_events");

export const personalEventsRepo = {
  watch(uid: string, cb: (list: PersonalEvent[]) => void, onError?: (e: Error & { code?: string }) => void): () => void {
    return onSnapshot(
      sub(uid),
      (snap) =>
        cb(
          snap.docs
            .map((d) => coercePersonalEvent(d.id, d.data()))
            .filter((e): e is PersonalEvent => e !== null),
        ),
      onError,
    );
  },

  async add(uid: string, input: PersonalEventInput): Promise<string> {
    const payload: Record<string, unknown> = {
      kind: input.kind,
      done: input.kind === "todo" && input.done,
      title: input.title,
      date: input.date,
      all_day: input.all_day,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    };
    // Firestore 는 undefined 를 거부 — 값이 있을 때만 넣는다
    if (!input.all_day && input.start_time) payload.start_time = input.start_time;
    if (!input.all_day && input.end_time) payload.end_time = input.end_time;
    if (input.memo) payload.memo = input.memo;
    if (input.calendar_id) payload.calendar_id = input.calendar_id;
    const ref = await addDoc(sub(uid), payload);
    return ref.id;
  },

  async update(uid: string, id: string, input: PersonalEventInput): Promise<void> {
    await updateDoc(doc(db, "users", uid, "personal_events", id), {
      kind: input.kind,
      done: input.kind === "todo" && input.done,
      title: input.title,
      date: input.date,
      all_day: input.all_day,
      start_time: !input.all_day && input.start_time ? input.start_time : deleteField(),
      end_time: !input.all_day && input.end_time ? input.end_time : deleteField(),
      memo: input.memo ? input.memo : deleteField(),
      calendar_id: input.calendar_id ? input.calendar_id : deleteField(),
      updated_at: serverTimestamp(),
    });
  },

  async remove(uid: string, id: string): Promise<void> {
    await deleteDoc(doc(db, "users", uid, "personal_events", id));
  },
};
